import { jsx } from "panel";
import {
  MultiAttributionType,
  RemoveAttributionValue
} from "common/widgets/multi-attribution-menu/types";
import { InputEl } from "common/components/input";
import { InputSize, InputType, ValidationStatus } from "common/components/input/types";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { ButtonEl } from "common/components/button";
import { ButtonTheme } from "common/components/button/constants";
import { AttributionTypeToSpec } from "common/widgets/multi-attribution-menu/constants";
import { InputLabelEl } from "common/components/input/label";
function renderWeightInputs({
  disabled,
  menu,
  weights,
  updateFocusedWeightIndex,
  invalidWeightIndex,
  invalidWeightsText
}) {
  if (!weights) {
    return null;
  }
  const { first, middle, last } = weights;
  const inputSpecs = [
    { weight: first, label: `First %` },
    { weight: middle, label: `Middle %` },
    { weight: last, label: `Last %` }
  ];
  return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".attribution-weights-section",
    attrs: {
      elRef: `multiAttributionWeightsSection`
    }
  }, inputSpecs.map((inputSpec, weightIndex) => {
    var _a;
    return /* @__PURE__ */ jsx(InputEl, {
      sel: ".attribution-weight-input",
      attrs: {
        disabled,
        label: inputSpec.label,
        size: InputSize.Medium,
        type: InputType.Number,
        min: `0`,
        maxlength: 3,
        elRef: `multiAttributionWeightsInput`,
        "validation-status": invalidWeightIndex === weightIndex ? ValidationStatus.Invalid : ValidationStatus.Default
      },
      on: {
        input: (ev) => menu.onWeightChange({ ev, weightIndex }),
        focus: () => updateFocusedWeightIndex(weightIndex)
      },
      props: {
        value: (_a = inputSpec.weight) == null ? void 0 : _a.toString()
      }
    });
  })), invalidWeightsText && /* @__PURE__ */ jsx("div", {
    attrs: { elRef: `attributionValidationError` },
    sel: ".attribution-validation-error"
  }, invalidWeightsText));
}
export function template() {
  const focusedAttributionType = this.state.focusedAttributionType;
  const focusedAttribution = AttributionTypeToSpec[focusedAttributionType];
  const stagedMultiAttributionWeights = this.getWeights();
  const focusedAttributionTypeWeights = focusedAttributionType ? this.getWeightsForAttributionType(focusedAttributionType) : null;
  const sectionsWithItems = this.getAttributionTypeSections();
  const focusedAttributionTypeIsCustom = focusedAttributionType === MultiAttributionType.Custom;
  const multiAttributionIsCustom = (multiAttribution) => multiAttribution.type === MultiAttributionType.Custom;
  const updateFocusedWeightIndex = (index) => {
    this.update({
      lastFocusedWeightIndex: index
    });
  };
  const invalidWeightsText = this.state.invalidWeightsText;
  const invalidWeightIndex = invalidWeightsText && this.state.lastFocusedWeightIndex;
  function ContextPanelContent({ menu }) {
    if (!focusedAttributionType) {
      return null;
    }
    if (!menu.attributionTypeIsEnabledForProject(focusedAttributionType)) {
      return /* @__PURE__ */ jsx("div", {
        sel: ".attribution-dropdown-content"
      }, /* @__PURE__ */ jsx("div", {
        sel: ".attribution-dropdown-title"
      }, "Advanced Attribution Models"), /* @__PURE__ */ jsx("div", {
        sel: ".attribution-dropdown-description"
      }, "Try out the power of advanced attribution models by upgrading your", ` `, /* @__PURE__ */ jsx("a", {
        attrs: { href: `https://mixpanel.com/pricing/`, target: `_blank` }
      }, "Mixpanel plan"), "."), /* @__PURE__ */ jsx("div", {
        sel: ".attribution-dropdown-action"
      }, /* @__PURE__ */ jsx(ButtonEl, {
        attrs: { theme: ButtonTheme.Primary },
        on: { click: () => window.open(`https://docs.mixpanel.com/docs/analysis/advanced/attribution`, `_blank`) }
      }, "Learn More")));
    } else if (!menu.focusedAttributionIsDisabled) {
      return /* @__PURE__ */ jsx("div", {
        sel: ".attribution-dropdown-content"
      }, /* @__PURE__ */ jsx("div", {
        sel: ".attribution-dropdown-title"
      }, focusedAttribution.label), /* @__PURE__ */ jsx("div", {
        sel: ".attribution-dropdown-description"
      }, focusedAttribution.description), focusedAttributionTypeWeights && !focusedAttributionTypeIsCustom && renderWeightInputs({
        menu,
        weights: focusedAttributionTypeWeights,
        disabled: true,
        updateFocusedWeightIndex,
        invalidWeightIndex,
        invalidWeightsText
      }));
    }
    return null;
  }
  return /* @__PURE__ */ jsx("div", {
    attrs: {
      elRef: `multiAttributionMenu`
    },
    sel: ".attribution-content",
    hook: { insert: (vnode) => this.setTooltipPositionParentElement(vnode) }
  }, /* @__PURE__ */ jsx(InputLabelEl, {
    attrs: { label: `Model` }
  }), /* @__PURE__ */ jsx(SelectEl, {
    sel: ".attribution-type-select",
    attrs: {
      theme: SelectTheme.Secondary,
      "menu-height": 450,
      "disable-search": true,
      open: !multiAttributionIsCustom(this.state.stagedMultiAttribution),
      "start-focus-on-selected": true
    },
    props: {
      sections: sectionsWithItems,
      contextPanelPositionParentElement: this.getTooltipPositionParentElement()
    },
    on: {
      change: (ev) => {
        if (ev.detail.item.value === RemoveAttributionValue) {
          this.dispatchDelete();
          return;
        }
        const attributionType = ev.detail.item.value;
        this.onAttributionTypeChange(attributionType);
        if (attributionType !== MultiAttributionType.Custom) {
          this.dispatchChange();
        }
      },
      focusItem: (event) => {
        this.updateFocusedAttributionType(event.detail.item);
      }
    }
  }, /* @__PURE__ */ jsx("div", {
    attrs: { slot: `context-panel-content` }
  }, /* @__PURE__ */ jsx(ContextPanelContent, {
    menu: this
  }))), multiAttributionIsCustom(this.state.stagedMultiAttribution) && /* @__PURE__ */ jsx(InputEl, {
    sel: ".attribution-custom-name-input",
    attrs: {
      label: `Name`,
      size: InputSize.Medium,
      elRef: `multiAttributionNameInput`
    },
    on: {
      change: (ev) => this.onCustomNameChange(ev)
    },
    props: {
      value: this.state.stagedMultiAttribution.name
    }
  }), stagedMultiAttributionWeights && multiAttributionIsCustom(this.state.stagedMultiAttribution) && renderWeightInputs({
    menu: this,
    weights: stagedMultiAttributionWeights,
    disabled: false,
    updateFocusedWeightIndex,
    invalidWeightIndex,
    invalidWeightsText
  }), /* @__PURE__ */ jsx("div", {
    sel: ".attribution-footer-section"
  }, /* @__PURE__ */ jsx(ButtonEl, {
    attrs: { theme: ButtonTheme.Tertiary },
    on: { click: () => this.dispatchCancel() }
  }, "Cancel"), /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      elRef: `multiAttributionApplyButton`,
      theme: ButtonTheme.Primary,
      disabled: Boolean(invalidWeightsText)
    },
    on: { click: () => this.dispatchChange() }
  }, "Apply")));
}
